import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@verlo/shared/components/ui/command';
import { sitesApi, staffApi, menuApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { useHasTill } from '../../contexts/SiteContext';
import { PAGES, pageSuitsPlan } from './areas';

/**
 * Jump (⌘K / Ctrl+K): the nav's escape hatch (Back Office Review, wave A). Type a page, a
 * site, a person or a product and go. Pages come from `areas.js`; the rest is fetched once
 * when the palette first opens, so the first keystroke is instant and the second is complete.
 */
export function useJump() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen((o) => !o); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return { open, setOpen };
}

const Jump = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  // The sidebar hides the till-shaped pages at a back-office site; ⌘K has to agree, or
  // the one path into them that is left is the fastest one.
  const hasTill = useHasTill();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || data) return;
    Promise.allSettled([sitesApi.list(), staffApi.getAll(true), menuApi.getAll()]).then(([s, st, m]) => {
      setData({
        sites: s.status === 'fulfilled' ? s.value.data || [] : [],
        staff: st.status === 'fulfilled' ? st.value.data || [] : [],
        products: m.status === 'fulfilled' ? m.value.data || [] : [],
      });
    });
  }, [open, data]);

  const pages = useMemo(
    () => PAGES.filter((p) => (!p.perm || !hasPermission || hasPermission(p.perm)) && pageSuitsPlan(p, hasTill)),
    [hasPermission, hasTill]);
  const go = (to) => { onOpenChange(false); navigate(to); };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Jump to a page, site, person or product…" data-testid="jump-input" />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>Nothing by that name.</CommandEmpty>
        <CommandGroup heading="Pages">
          {pages.map((p) => (
            <CommandItem key={p.to} value={`${p.label} ${p.area.label} ${p.hint || ''}`} onSelect={() => go(p.to)}>
              <span className="font-medium">{p.label}</span>
              <span className="ml-2 text-[12px] text-ink-faint">{p.area.label}{p.group ? ` · ${p.group}` : ''}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        {data?.sites?.length > 0 && (
          <CommandGroup heading="Sites">
            {data.sites.map((s) => (
              <CommandItem key={s.id} value={`site ${s.name}`} onSelect={() => go(`/back-office/sites`)}>{s.name}</CommandItem>
            ))}
          </CommandGroup>
        )}
        {data?.staff?.length > 0 && (
          <CommandGroup heading="People">
            {data.staff.slice(0, 200).map((p) => (
              <CommandItem key={p.id} value={`person ${p.name} ${p.role || ''}`} onSelect={() => go(`/back-office/team?q=${encodeURIComponent(p.name)}`)}>
                {p.name}<span className="ml-2 text-[12px] text-ink-faint">{p.role}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {data?.products?.length > 0 && (
          <CommandGroup heading="Products">
            {data.products.slice(0, 300).map((m) => (
              <CommandItem key={m.id} value={`product ${m.name} ${m.category || ''}`} onSelect={() => go(`/back-office/menu?item=${m.id}`)}>
                {m.name}<span className="ml-2 text-[12px] text-ink-faint">{m.category}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default Jump;
